import { useCallback, useEffect, useState } from "react";
import {
  BookOpen,
  Compass,
  Cpu,
  Sun,
  Factory,
  ArrowRight,
  CheckCircle2,
  Circle,
  Lock,
  MessageSquare,
  FolderKanban,
  Wrench,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { chatIdForProject, touchChatEntry } from "@/lib/prometheusChatIndex";
import {
  UNIVERSITY_TRACKS,
  LEVEL_LABEL,
  LEVEL_ORDER,
  computeTrackEvidence,
  getModuleStatus,
  getTrackById,
  modulesByLevel,
  practiceLabel,
  setModuleQuizPassed,
  suggestProjectForTrack,
  type TrackEvidence,
  type UniversityModule,
  type UniversityTrack,
  type UniversityTrackId,
} from "@/lib/prometheusUniversity";

const ICONS = {
  mech: Factory,
  elec: Cpu,
  energy: Sun,
  ai: Compass,
} as const;

function openProjectArtifact(projectId: string, tab?: string) {
  window.dispatchEvent(
    new CustomEvent("prometheus-open-project", {
      detail: { projectId, tab },
    })
  );
}

function continueModuleInChat(
  track: UniversityTrack,
  module: UniversityModule,
  projectId?: string | null
) {
  const skill = module.chatSkill || track.chatSkill || "";
  const focus = module.chatFocus || module.title;
  const prompt = `${skill ? `${skill} ` : ""}Universidad · track «${track.title}» · módulo «${module.title}».
${focus}
Enséñame con física/math de taller y un ejercicio concreto sobre mi proyecto${
    projectId ? ` (id ${projectId})` : ""
  }. Si solo debo entender, quédate en explicación; si hay que mutar wiring/hardware, dime que pase a Chat y pida OK.`;

  if (projectId) {
    touchChatEntry({
      id: chatIdForProject(projectId),
      projectId,
      title: `${track.title} · ${module.title}`,
    });
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
  }

  window.dispatchEvent(
    new CustomEvent("prometheus-os-section", { detail: { section: "chats" } })
  );
  window.dispatchEvent(
    new CustomEvent("open-prometheus-chat", {
      detail: {
        projectId: projectId || undefined,
        characterId: "prometheus",
        initialPrompt: prompt,
        autoSend: true,
      },
    })
  );
}

function EvidenceBar({ evidence }: { evidence: TrackEvidence }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground uppercase tracking-wider font-bold">
          Evidencia de taller
        </span>
        <span className="text-primary font-bold">{evidence.evidenceScore}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-primary/80 transition-[width]"
          style={{ width: `${evidence.evidenceScore}%` }}
        />
      </div>
      <ul className="grid grid-cols-2 gap-1.5 text-[10px] text-muted-foreground">
        <li>
          Pasos {evidence.stepsDone}/{evidence.stepsTotal || "—"}
        </li>
        <li>
          Wiring {evidence.wiringDone}/{evidence.wiringTotal || "—"}
        </li>
        <li>Monitor online: {evidence.onlineDevices}</li>
        <li>
          Módulos {evidence.modulesUnderstood}/{evidence.modulesTotal}
        </li>
      </ul>
    </div>
  );
}

/** Horizontal curriculum spine: Fundamentos → Aplicado → Especialidad */
function CurriculumSpine({
  track,
  evidence,
  activeModuleId,
  onSelectModule,
}: {
  track: UniversityTrack;
  evidence: TrackEvidence;
  activeModuleId: string | null;
  onSelectModule: (id: string) => void;
}) {
  const byLevel = modulesByLevel(track);
  return (
    <div className="rounded-xl border border-border/70 bg-muted/15 p-3 space-y-3">
      <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
        Curriculum · grafo de niveles
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-2">
        {LEVEL_ORDER.map((level, li) => {
          const mods = byLevel[level];
          const done = mods.filter((m) => getModuleStatus(track, m, evidence).quizPassed).length;
          return (
            <div key={level} className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                {li > 0 ? (
                  <ArrowRight className="w-3 h-3 text-muted-foreground/50 hidden sm:block shrink-0 -ml-1" />
                ) : null}
                <p className="text-[10px] font-bold text-foreground">
                  {LEVEL_LABEL[level]}
                  <span className="text-muted-foreground font-normal ml-1">
                    {done}/{mods.length}
                  </span>
                </p>
              </div>
              <ul className="space-y-1">
                {mods.map((m) => {
                  const st = getModuleStatus(track, m, evidence);
                  const active = activeModuleId === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => onSelectModule(m.id)}
                        className={cn(
                          "w-full flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[10px] text-left cursor-pointer transition-colors",
                          active
                            ? "border-primary/50 bg-primary/15 text-primary font-bold"
                            : st.complete
                              ? "border-emerald-500/30 bg-emerald-500/5 text-foreground"
                              : st.unlocked
                                ? "border-border/60 text-foreground hover:bg-muted/40"
                                : "border-border/40 text-muted-foreground/70"
                        )}
                      >
                        {!st.unlocked ? (
                          <Lock className="w-3 h-3 shrink-0" />
                        ) : st.quizPassed ? (
                          <CheckCircle2 className="w-3 h-3 shrink-0 text-emerald-400" />
                        ) : (
                          <Circle className="w-3 h-3 shrink-0 opacity-50" />
                        )}
                        <span className="truncate">{m.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModuleQuiz({
  module,
  passed,
  locked,
  onPassed,
}: {
  module: UniversityModule;
  passed: boolean;
  locked: boolean;
  onPassed: () => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(passed);

  useEffect(() => {
    setPicked(null);
    setRevealed(passed);
  }, [module.id, passed]);

  if (locked) {
    return (
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Lock className="w-3.5 h-3.5" />
        Completa el módulo anterior (quiz) para desbloquear esta comprensión.
      </p>
    );
  }

  const submit = () => {
    if (picked == null) {
      toast.message("Elige una opción");
      return;
    }
    setRevealed(true);
    if (picked === module.quiz.correctIndex) {
      setModuleQuizPassed(module.id, true);
      onPassed();
      toast.success("Comprendido — evidencia del módulo (sin XP)");
    } else {
      toast.message("Revisa la lección e inténtalo de nuevo");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-background/50 p-3 space-y-2.5">
      <p className="text-[9px] uppercase tracking-[0.14em] text-amber-400/90 font-bold">
        Quiz del módulo · sin XP
      </p>
      <p className="text-[12px] text-foreground font-medium leading-snug">{module.quiz.question}</p>
      <ul className="space-y-1">
        {module.quiz.options.map((opt, i) => {
          const selected = picked === i;
          const showCorrect = revealed && i === module.quiz.correctIndex;
          const showWrong = revealed && selected && i !== module.quiz.correctIndex;
          return (
            <li key={opt}>
              <button
                type="button"
                disabled={revealed && passed}
                onClick={() => {
                  if (revealed && passed) return;
                  setPicked(i);
                  setRevealed(false);
                }}
                className={cn(
                  "w-full text-left rounded-md border px-2.5 py-1.5 text-[11px] cursor-pointer transition-colors",
                  showCorrect && "border-emerald-500/50 bg-emerald-500/10 text-emerald-200",
                  showWrong && "border-red-500/40 bg-red-500/10 text-red-200",
                  !showCorrect &&
                    !showWrong &&
                    selected &&
                    "border-primary/50 bg-primary/10 text-foreground",
                  !showCorrect &&
                    !showWrong &&
                    !selected &&
                    "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30"
                )}
              >
                {opt}
              </button>
            </li>
          );
        })}
      </ul>
      {revealed ? (
        <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border/40 pt-2">
          {module.quiz.explain}
        </p>
      ) : (
        <button
          type="button"
          onClick={submit}
          className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-bold cursor-pointer hover:opacity-90"
        >
          Comprobar
        </button>
      )}
    </div>
  );
}

function ModuleDetail({
  track,
  module,
  evidence,
  projectId,
  onRefresh,
}: {
  track: UniversityTrack;
  module: UniversityModule;
  evidence: TrackEvidence;
  projectId?: string | null;
  onRefresh: () => void;
}) {
  const st = getModuleStatus(track, module, evidence);
  const practiceHint = practiceLabel(module.practice ?? null);

  return (
    <div className="rounded-xl border border-border bg-card/50 p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[9px] uppercase tracking-[0.14em] text-primary font-bold">
            {LEVEL_LABEL[module.level]}
          </p>
          <h3 className="text-sm font-bold text-foreground mt-0.5">{module.title}</h3>
          <p className="text-[11px] text-muted-foreground mt-1">{module.detail}</p>
        </div>
        <div className="flex flex-wrap gap-1 text-[9px]">
          {st.quizPassed ? (
            <span className="rounded-md border border-emerald-500/40 px-1.5 py-0.5 text-emerald-400">
              Quiz OK
            </span>
          ) : null}
          {st.practiceMet ? (
            <span className="rounded-md border border-sky-500/40 px-1.5 py-0.5 text-sky-400">
              Práctica OK
            </span>
          ) : practiceHint ? (
            <span className="rounded-md border border-border px-1.5 py-0.5 text-muted-foreground">
              Práctica pendiente
            </span>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-void/40 px-3 py-2.5 space-y-2">
        <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" />
          Lección
        </p>
        {module.lesson.split("\n").map((para) =>
          para.trim() ? (
            <p key={para.slice(0, 24)} className="text-[12px] text-foreground/90 leading-relaxed font-sans">
              {para.trim()}
            </p>
          ) : null
        )}
      </div>

      {practiceHint ? (
        <p className="text-[11px] text-muted-foreground flex items-start gap-2">
          <Wrench className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400/90" />
          <span>
            Práctica de taller: {practiceHint}
            {!st.practiceMet ? " — aún no detectada en tus proyectos." : " — detectada."}
          </span>
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {projectId && module.artifactTab ? (
          <button
            type="button"
            onClick={() => openProjectArtifact(projectId, module.artifactTab)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/15 text-primary px-3 py-2 text-[11px] font-bold cursor-pointer hover:bg-primary/25"
          >
            <FolderKanban className="w-3.5 h-3.5" />
            Practicar en Artifact
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => continueModuleInChat(track, module, projectId)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-[11px] font-bold cursor-pointer hover:opacity-90"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          Lección en chat
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <ModuleQuiz
        module={module}
        passed={st.quizPassed}
        locked={!st.unlocked}
        onPassed={onRefresh}
      />
    </div>
  );
}

/**
 * Universidad panel — curriculum por niveles, lecciones, quizzes por módulo, sin XP.
 */
export function UniversidadPanel() {
  const [activeId, setActiveId] = useState<UniversityTrackId>("elec");
  const [activeModuleId, setActiveModuleId] = useState<string | null>("elec-pins");
  const [evidenceByTrack, setEvidenceByTrack] = useState<Record<string, TrackEvidence>>({});

  const refresh = useCallback(() => {
    const next: Record<string, TrackEvidence> = {};
    for (const t of UNIVERSITY_TRACKS) {
      next[t.id] = computeTrackEvidence(t);
    }
    setEvidenceByTrack(next);
  }, []);

  useEffect(() => {
    refresh();
    const sync = () => refresh();
    window.addEventListener("prometheus-university-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("prometheus-university-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, [refresh]);

  const track = getTrackById(activeId) ?? UNIVERSITY_TRACKS[0];
  const evidence = evidenceByTrack[track.id] ?? computeTrackEvidence(track);
  const Icon = ICONS[track.id];
  const suggested = suggestProjectForTrack(track);
  const activeModule =
    track.modules.find((m) => m.id === activeModuleId) || track.modules[0];

  const selectTrack = (id: UniversityTrackId) => {
    setActiveId(id);
    const t = getTrackById(id);
    setActiveModuleId(t?.modules[0]?.id ?? null);
  };

  return (
    <div className="h-full overflow-y-auto bg-void p-4 pt-12 sm:p-6 md:pt-6 font-mono-tech">
      <div className="max-w-3xl mx-auto space-y-5">
        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">
            Universidad práctica
          </p>
          <h1 className="font-serif-display text-2xl text-foreground">
            Curriculum · evidencia · acción
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Niveles Fundamentos → Aplicado → Especialidad. Lecciones en panel, quiz por módulo,
            práctica en Artifact y profundidad en chat. Sin puntos XP.
          </p>
        </header>

        <div className="flex flex-wrap gap-1.5">
          {UNIVERSITY_TRACKS.map((t) => {
            const active = t.id === activeId;
            const ev = evidenceByTrack[t.id];
            const TrackIcon = ICONS[t.id];
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTrack(t.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] cursor-pointer transition-colors",
                  active
                    ? "border-primary/50 bg-primary/15 text-primary font-bold"
                    : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30"
                )}
              >
                <TrackIcon className="w-3 h-3" />
                {t.title.split("&")[0].trim()}
                {ev && ev.modulesUnderstood > 0 ? (
                  <span className="text-[9px] opacity-80">
                    {ev.modulesUnderstood}/{ev.modulesTotal}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <article className="rounded-xl border border-border bg-card/40 p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg border border-primary/25 bg-primary/10 p-2">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-foreground">{track.title}</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                {track.blurb}
              </p>
            </div>
          </div>

          {track.safetyNote ? (
            <p className="text-[11px] text-amber-200/90 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {track.safetyNote}
            </p>
          ) : null}

          <EvidenceBar evidence={evidence} />

          <CurriculumSpine
            track={track}
            evidence={evidence}
            activeModuleId={activeModule?.id ?? null}
            onSelectModule={setActiveModuleId}
          />

          {evidence.matchedProjects.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Proyectos vinculados
              </p>
              <ul className="space-y-1">
                {evidence.matchedProjects.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => openProjectArtifact(p.id)}
                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-border/50 px-2.5 py-1.5 text-[11px] hover:bg-muted/30 cursor-pointer text-left"
                    >
                      <span className="truncate text-foreground">{p.title}</span>
                      <span className="text-muted-foreground shrink-0">{p.progress}%</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </article>

        {activeModule ? (
          <ModuleDetail
            track={track}
            module={activeModule}
            evidence={evidence}
            projectId={suggested?.id}
            onRefresh={refresh}
          />
        ) : null}
      </div>
    </div>
  );
}
